import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import * as SelectPrimitive from '@radix-ui/react-select'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

import { SelectOptionsSearchInput } from './SelectOptionsSearchInput'

type SelectWithSearchProps = {
  searchPlaceholder: string
  value?: string
  defaultValue?: string
  options: Array<Record<string, string>>
  onValueChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  noRecordMessage?: string
  noButton?: boolean
}

export const SelectWithSearch = ({
  searchPlaceholder,
  value,
  defaultValue,
  options,
  onValueChange,
  placeholder,
  disabled,
  noRecordMessage,
  noButton,
}: SelectWithSearchProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [triggerWidth, setTriggerWidth] = useState<number>()
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredOptions, setFilteredOptions] = useState(options)

  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
      setFilteredOptions(options)
    }
  }, [isOpen])

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  const handleSearch = () => {
    const newFilteredOptions = options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.label === value
    )

    setFilteredOptions(newFilteredOptions)
    searchInputRef.current?.focus()
  }

  useEffect(() => {
    handleSearch()
  }, [searchTerm])

  return (
    <SelectPrimitive.Root
      open={isOpen}
      onOpenChange={setIsOpen}
      value={value}
      onValueChange={(value) => {
        onValueChange(value)
      }}
      defaultValue={defaultValue}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger asChild>
        <button
          className="flex items-center justify-between w-full p-3 text-xs rounded-lg outline-none border-grey-500 border-1 text-grey-800 disabled:bg-grey-300 disabled:text-grey-500 data-[placeholder]:text-grey-500 data-[placeholder]:font-sans lg:py-4 lg:text-sm"
          ref={(node) => node && setTriggerWidth(node.offsetWidth)}
          onClick={(event) => event.stopPropagation()}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon>
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </SelectPrimitive.Icon>
        </button>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Content
        position="popper"
        sideOffset={4}
        className="z-50 overflow-hidden border rounded-md border-primary-light"
        style={{ width: triggerWidth || undefined }}
        onClick={(event) => event.stopPropagation()}
      >
        <SelectPrimitive.Viewport className="bg-white shadow-lg">
          <SelectPrimitive.Group>
            <SelectOptionsSearchInput
              placeholder={searchPlaceholder}
              value={searchTerm}
              onValueChange={(newValue) => setSearchTerm(newValue)}
              onSearch={handleSearch}
              noButton={noButton}
            />

            <div className="mt-1 overflow-y-scroll max-h-64 md:max-h-64">
              {filteredOptions?.length === 0 ? (
                <p className="flex items-center justify-center h-16 text-xs font-semibold text-center opacity-50 md:text-sm text-grey-700">
                  {noRecordMessage || 'Nenhum registro encontrado'}
                </p>
              ) : (
                <>
                  {filteredOptions?.map(({ label, value }) => (
                    <SelectPrimitive.Item
                      key={value}
                      value={value}
                      className={clsx(
                        'data-[state="checked"]:bg-primary-light h-fit font-normal data-[state="checked"]:font-medium data-[state="checked"]:text-white relative flex items-center px-4 py-3 text-sm text-gray-700 ',
                        'radix-disabled:opacity-50',
                        'focus:outline-none cursor-pointer'
                      )}
                    >
                      <SelectPrimitive.ItemText>
                        {label}
                      </SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                  ))}
                </>
              )}
            </div>
          </SelectPrimitive.Group>
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Root>
  )
}
